import { notifyConnectedTaskJournalChanged, subscribeConnectedTaskJournalChanged } from "@/lib/connectedTaskJournalBus";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import { claimConnectorWrite, finishConnectorWrite, loadConnectorWrite, type ConnectorWriteBody, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import { CONNECTED_TASK_WRITE_CONTRACT, connectedTaskCommitBody, connectedTaskJournalBody, connectedTaskOwnerSession, connectedTaskReceiptBody, invokeConnectedTaskWrite,
  validateConnectedTaskJournal, validateConnectedTaskReply, verifyConnectedTaskReplyContent, validatePreparedConnectedTaskWrite, verifyPreparedConnectedTaskContent, type ConnectedTaskReply, type ConnectedTaskJournalBody, type PreparedConnectedTaskWrite } from "@/lib/connectedTaskExecutor";

export function useConnectedTaskWrite(ownerId: string, actionId: string, mirrorSessionId: string | undefined,
  surfaceQuota: (error: unknown) => Promise<boolean>, originCurrent?: () => boolean, enabled = true) {
  const [pending, setPending] = useState<PendingConnectorWrite | null>(null), [review, setReview] = useState<PreparedConnectedTaskWrite | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const generation = useRef(0), lock = useRef(false), controller = useRef<AbortController | null>(null), sid = useRef<string | null>(null);
  const preparedBody = useRef<ConnectedTaskJournalBody | null>(null), activeOrigin = useRef(originCurrent);
  activeOrigin.current = originCurrent;
  const current = useCallback((epoch: number) => generation.current === epoch && (activeOrigin.current?.() ?? true), []);
  const belongsToCurrent = (entry: PendingConnectorWrite) => entry.body.idempotency_key === (mirrorSessionId
    ? "mirror_connected_task_" + actionId + "_" + entry.body.action : "connected_task:" + actionId + ":" + entry.body.action) &&
    (mirrorSessionId ? (entry.body.mirror_context as { session_id?: string } | undefined)?.session_id === mirrorSessionId : entry.body.mirror_context === undefined);
  useEffect(() => {
    const epoch = ++generation.current;
    sid.current = null; lock.current = false; preparedBody.current = null; controller.current?.abort();
    setBusy(false); setReady(false); setPending(null); setReview(null); setError(null);
    if (!enabled) { const inactiveGeneration = generation; setReady(true); return () => { inactiveGeneration.current++; }; }
    void (async () => {
      const session = await connectedTaskOwnerSession(ownerId);
      if (!current(epoch)) return;
      sid.current = session.sid;
      const entry = await loadConnectorWrite(ownerId, "connected_task");
      await connectedTaskOwnerSession(ownerId, session.sid);
      if (!current(epoch)) return;
      if (entry && (!validateConnectedTaskJournal(entry, ownerId) || entry.body.auth_session_id !== session.sid)) throw new Error("prior_connected_task_action");
      // A stored succeeded flag has no authority after a reload.
      setPending(entry ? { ...entry, status: "pending", confirmedResult: undefined } : null);
      setReady(true);
    })().catch(cause => { if (current(epoch)) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextSid = session?.access_token ? connectedFileReadSessionId(session.access_token) : null;
      if (session?.user.id === ownerId && (sid.current === null || nextSid === sid.current)) return;
      generation.current++; lock.current = true; controller.current?.abort(); preparedBody.current = null;
      setReady(false); setBusy(false); setReview(null); setPending(null); setError("invalid_session");
    });
    const lifecycle = generation, activeController = controller;
    return () => { subscription.unsubscribe(); lifecycle.current++; activeController.current?.abort(); };
  }, [ownerId, actionId, mirrorSessionId, current, enabled]);
  useEffect(() => {
    const epoch = generation.current;
    return subscribeConnectedTaskJournalChanged(changedOwner => {
      if (changedOwner !== ownerId || !current(epoch) || lock.current || !sid.current) return;
      void (async () => {
        await connectedTaskOwnerSession(ownerId, sid.current!);
        const stored = await loadConnectorWrite(ownerId, "connected_task");
        if (current(epoch) && !stored) { setPending(null); setReview(null); preparedBody.current = null; setError(null); setReady(true); }
      })().catch(() => { if (current(epoch)) setError("invalid_session"); });
    });
  }, [ownerId, actionId, mirrorSessionId, current]);
  const captureCurrent = () => { const epoch = generation.current; return () => ready && current(epoch); };
  const transport = async (body: Record<string, unknown>, epoch: number) => {
    if (!sid.current || !current(epoch)) throw new Error("invalid_session");
    const aborter = new AbortController(); controller.current = aborter;
    const timeout = setTimeout(() => aborter.abort(), 60_000);
    try { return await invokeConnectedTaskWrite(ownerId, sid.current, body, { signal: aborter.signal, isCurrent: () => current(epoch), surfaceQuota }); }
    finally { clearTimeout(timeout); }
  };
  const prepare = async (create: () => Promise<ConnectorWriteBody> | ConnectorWriteBody) => {
    if (lock.current || !ready || pending) return null;
    const epoch = generation.current; lock.current = true; setBusy(true); setError(null);
    try {
      const payload = await create(); if (!current(epoch) || !sid.current) throw new Error("invalid_session");
      const request = { ...payload, write_contract: CONNECTED_TASK_WRITE_CONTRACT, phase: "prepare", expected_owner_id: ownerId };
      const response = await transport(request, epoch);
      if (!validatePreparedConnectedTaskWrite(response, payload, ownerId, sid.current) || !await verifyPreparedConnectedTaskContent(response, payload)) throw new Error("outcome_unknown");
      await connectedTaskOwnerSession(ownerId, sid.current);
      if (!current(epoch)) return null;
      preparedBody.current = connectedTaskJournalBody(payload, response); setReview(response);
      return response;
    } catch (cause) { if (current(epoch)) setError(cause instanceof Error ? cause.message : "outcome_unknown"); return null; }
    finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const recover = async () => {
    if (lock.current || !ready || !pending || !validateConnectedTaskJournal(pending, ownerId)) return null;
    const epoch = generation.current; lock.current = true; setBusy(true); setError(null);
    try {
      const response = await transport(connectedTaskReceiptBody(pending.body), epoch);
      if (!validateConnectedTaskReply(response, pending.body, "receipt_status")) throw new Error("outcome_unknown");
      if (response.status === "failed" && response.safe_retry === true) {
        await finishConnectorWrite(pending, true, () => { if (!current(epoch)) throw new Error("invalid_session"); });
        if (current(epoch)) { setPending(null); setReview(null); preparedBody.current = null; setError("connected_task_safe_retry"); notifyConnectedTaskJournalChanged(ownerId); }
        return null;
      }
      if (response.status !== "succeeded" || !await verifyConnectedTaskReplyContent(response) || !current(epoch)) throw new Error("outcome_unknown");
      await connectedTaskOwnerSession(ownerId, pending.body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      await finishConnectorWrite(pending, false, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return null;
      const completed = { ...pending, status: "succeeded" as const, confirmedResult: response };
      setPending(completed); return completed;
    } catch (cause) { if (current(epoch)) setError(cause instanceof Error ? cause.message : "outcome_unknown"); return null; }
    finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const execute = async () => {
    if (pending) return recover();
    if (lock.current || !ready || !preparedBody.current || !review) return null;
    const epoch = generation.current, body = structuredClone(preparedBody.current);
    lock.current = true; setBusy(true); setError(null);
    try {
      if (Date.parse(body.expires_at) <= Date.now()) throw new Error("review_expired");
      await connectedTaskOwnerSession(ownerId, body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      const claimed = await claimConnectorWrite(ownerId, "connected_task", body, { title: body.review.target.destination_name || body.review.title }, { authSessionId: body.auth_session_id });
      if (!current(epoch)) throw new Error("invalid_session");
      if (!claimed.created || JSON.stringify(claimed.entry.body) !== JSON.stringify(body)) { setPending(claimed.entry); throw new Error("prior_connected_task_action"); }
      // Claim durably before any provider mutation; every later failure recovers only by lookup.
      setPending(claimed.entry); notifyConnectedTaskJournalChanged(ownerId);
      const response = await transport(connectedTaskCommitBody(body), epoch);
      if (!validateConnectedTaskReply(response, body, "commit") || response.status !== "succeeded" || !await verifyConnectedTaskReplyContent(response) || !current(epoch)) throw new Error("outcome_unknown");
      await connectedTaskOwnerSession(ownerId, body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      await finishConnectorWrite(claimed.entry, false, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return null;
      const completed = { ...claimed.entry, status: "succeeded" as const, confirmedResult: response };
      setPending(completed); setReview(null); return completed;
    } catch (cause) {
      if (current(epoch)) {
        const code = cause instanceof Error ? cause.message : "outcome_unknown";
        setError(code);
        if (code === "review_expired") { setReview(null); preparedBody.current = null; }
      }
      return null;
    } finally { if (current(epoch)) { lock.current = false; setBusy(false); } }
  };
  const discardReview = () => { if (busy || pending) return; preparedBody.current = null; setReview(null); setError(null); };
  const acknowledge = async () => {
    if (!pending || pending.status !== "succeeded" || !validateConnectedTaskJournal(pending, ownerId) ||
      !validateConnectedTaskReply(pending.confirmedResult, pending.body, (pending.confirmedResult as { phase?: string })?.phase === "commit" ? "commit" : "receipt_status")) return false;
    const epoch = generation.current;
    try {
      if (!await verifyConnectedTaskReplyContent(pending.confirmedResult as ConnectedTaskReply) || !current(epoch)) throw new Error("outcome_unknown");
      await connectedTaskOwnerSession(ownerId, pending.body.auth_session_id);
      if (!current(epoch)) throw new Error("invalid_session");
      await finishConnectorWrite(pending, true, () => { if (!current(epoch)) throw new Error("invalid_session"); });
      if (!current(epoch)) return false;
      setPending(null); setReview(null); preparedBody.current = null; notifyConnectedTaskJournalChanged(ownerId); return true;
    } catch (cause) { if (current(epoch)) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); return false; }
  };
  return { pending, review, ready, busy, error, prepare, execute, recover, acknowledge, discardReview, captureCurrent,
    prior: !!pending && !belongsToCurrent(pending), belongsToCurrent };
}
